import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, LogOut } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";

export default function Account() {
  const { user, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [myLeads, setMyLeads] = useState(null);

  const sendLink = async () => {
    try { await api.post("/auth/magic-link", { email }); setSent(true); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (user) {
    if (myLeads === null) {
      api.get("/my/leads").then((r) => setMyLeads(r.data)).catch(() => setMyLeads({ leads: [] }));
    }
    return (
      <div className="px-4 sm:px-6 max-w-3xl mx-auto py-12 md:py-20">
        <SEO title="My account — Tendly" />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Hi{user.name ? `, ${user.name}` : ""}</h1>
            <p className="text-sm text-muted-foreground mt-1">{user.email}</p>
          </div>
          <button data-testid="account-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
        <h2 className="mt-10 font-heading font-semibold text-xl">Your rental searches</h2>
        {!myLeads ? <p className="mt-4 text-sm text-muted-foreground">Loading…</p> : myLeads.leads.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-border bg-card p-8 text-center">
            <p className="text-sm text-muted-foreground">No searches yet under this email.</p>
            <Link to="/chat" data-testid="account-open-chat" className="mt-4 inline-block rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold">Start a search</Link>
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {myLeads.leads.map((l) => (
              <li key={l.id} data-testid={`account-lead-${l.id}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-sm">{(l.criteria?.locations || []).join(", ") || "Vancouver"} · ${l.budget_min?.toLocaleString()}–${l.budget_max?.toLocaleString()}/mo</p>
                  <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold capitalize">{l.status.replace(/_/g, " ")}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Started {l.created_at?.slice(0, 10)} · {l.selected_ids?.length || 0} homes selected</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 max-w-md mx-auto py-16 md:py-24">
      <SEO title="Renter account — Tendly" />
      <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
        <span className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center"><MailCheck className="w-6 h-6" /></span>
        <h1 className="mt-5 font-heading font-bold text-2xl tracking-tight">Your renter account</h1>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Save your searches and revisit your viewing requests. No password needed — we'll email you a secure sign-in link.</p>
        {sent ? (
          <p data-testid="magic-sent" className="mt-5 rounded-xl bg-secondary px-4 py-3 text-sm">If that email is registered or eligible, a sign-in link is on its way. Check your inbox.</p>
        ) : (
          <div className="mt-5 space-y-3">
            <input data-testid="magic-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" aria-label="Email address"
              className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm"
              onKeyDown={(e) => e.key === "Enter" && sendLink()} />
            <button data-testid="magic-send" onClick={sendLink} className="w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold">Email me a sign-in link</button>
          </div>
        )}
        <p className="mt-5 text-xs text-muted-foreground">We'll never ask for your password by email.</p>
      </div>
    </div>
  );
}
